import { useEffect, useEffectEvent, useState } from 'react';

import { Navigate, useLocation, useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import {
  IconCertificate,
  IconEdit,
  IconFilter,
  IconPrinter,
  IconUserPlus,
  IconUsersGroup,
} from '@tabler/icons-react';
import { useQueries } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { focusAtom } from 'jotai-optics';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import {
  getPlanAndAssignmentsOptions,
  getPlanReportOptions,
  getUsersForLocationsOptions,
  updateDueDate,
} from '@cfa/pathway-fe-shared/xp';
import { TextFieldType } from '@cfa/react-components';
import { MessageBlock } from '@cfa/react-core';
import { Show } from '@cfa/system-icons';

import useAuth from '@/hooks/useAuth';
import useGetStatusLabels from '@/hooks/useGetStatusLabels';
import { usePlanLocations } from '@/hooks/usePlanLocations';
import { useSortOptions } from '@/hooks/useSortOptions';

import { isValidPlanId as checkIsValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';
import {
  invalidateAllPlanQueries,
  invalidateCertificateQueries,
} from '@/utils/queryInvalidation';
import { isApiError } from '@/utils/request';
import { cn } from '@/utils/tailwind';
import { showAssignmentSuccessToast } from '@/utils/training-plans/show-assignment-success-toast';
import { withRoles } from '@/utils/withRoles';

import GenericError from '@/components/Error/GenericError';
import Filter, {
  FilterCheckboxSectionProps,
  FilterDesktop,
  FilterMobile,
  FilterSearchBarProps,
} from '@/components/Filter';
import { FilterAndSortButtonProps } from '@/components/Filter/components/FilterAndSortButton';
import { Header } from '@/components/header';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import TeamMemberCard from '@/components/PlanCards/TeamMemberCard';
import { planAssignmentsToTeamMemberRows } from '@/components/PlanCards/TeamMemberCard/helpers/planAssignmentsToTeamMemberRows';
import { planUserReportsToTeamMemberRows } from '@/components/PlanCards/TeamMemberCard/helpers/planUserReportsToTeamMemberRows';
import type { TeamMemberRow } from '@/components/PlanCards/TeamMemberCard/teamMemberRow';
import { PopoverMenu } from '@/components/popover-menu';
import { AssignTeamMembersModal } from '@/components/popups/AssignTeamMembersModal';
import { DueDatePopUp } from '@/components/popups/DueDatePopUp';
import { printPlan } from '@/components/print-report';
import {
  PrintCertificateModals,
  usePrintCertificates,
} from '@/components/PrintCertificates';
import SortFilterHeader from '@/components/SortFilterHeader/SortFilterHeader';
import ToastMessageBlock from '@/components/Toasts/SuccessToast';

import Constants, { routes } from '@/constants';

import AddTeamMemberFab from './AddTeamMemberFab';
import PlanInUseModal from './Modals/PlanInUseModal';
import UnassignTeamMember from './Modals/UnassignTeamMember';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';
import {
  DataViewAtomFamilyKey,
  sourceDataFamily,
  useDataView,
} from '@/atoms/data-view';
import { filtersToFilterInValues } from '@/atoms/data-view/helpers';
import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import {
  trainTogetherModeAtom,
  trainTogetherModeDispatchAtom,
} from '@/atoms/trainTogetherMode';
import { useAssignTeamMembers } from '@/routes/TrainingPlans/hooks/useAssignTeamMembers';
import TrainTogetherModal from '@/routes/TrainingPlans/ManagePlans/Modals/TrainTogetherModal';
import { hasActiveAssignees } from '@/routes/TrainingPlans/planBuildTimer';
const trainTogetherModeFocusAtom = focusAtom(trainTogetherModeAtom, o =>
  o.pick(['isTrainTogetherModeOpen', 'hasTeamMembersAssignedToPlan']),
);

const activePlanIdAtom = focusAtom(trainTogetherModeAtom, o =>
  o.prop('activePlanId'),
);

interface TeamMemberListProps {
  /** Is the view for reports route? @default false */
  reportsRoute?: boolean;
}

const PlanAssignees: React.FC<TeamMemberListProps> = ({
  reportsRoute = false,
}) => {
  const dataViewKey = reportsRoute
    ? DataViewAtomFamilyKey.REPORTS_PLAN_TEAM_MEMBERS
    : DataViewAtomFamilyKey.MANAGE_TEAM_MEMBERS;

  const dataSource = sourceDataFamily(dataViewKey);

  const sortKey = reportsRoute
    ? { reportsPlansPlanView: true }
    : { managePlanView: true };

  const { t } = useTranslation();
  const navigate = useNavigate();
  const track = useAmplitudeTrack();
  const statusLabels = useGetStatusLabels(dataViewKey);

  const location = useLocation();
  const { search } = location;
  const state = z
    .object({ refetch: z.boolean().optional() })
    .nullable()
    .catch(null)
    .parse(location.state as unknown);

  const { planId } = useParams<{ planId: string }>();
  const safePlanId = planId ?? '';

  const sortOptions = useSortOptions(sortKey);

  const [
    {
      display: { total, items },
      filters,
      sort,
    },
    {
      resetFilters,
      setSort,
      toggleCheckboxFilter,
      resetAllCheckboxFilters,
      setSearch,
      resetSearch,
    },
  ] = useDataView<TeamMemberRow>(dataViewKey);

  const currentFilters = filtersToFilterInValues(filters);

  const { data: auth } = useAuth();
  const locationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  // Certificate button hook
  const certificateHook = usePrintCertificates({ planId: safePlanId });

  const { hasTeamMembersAssignedToPlan } = useAtomValue(
    trainTogetherModeFocusAtom,
  );

  const activeLicenseeLocation = useAtomValue(
    activeLicenseeLocationAtom,
  )?.number;

  const patchTrainTogetherMode = useSetAtom(trainTogetherModeAtom);

  const setActivePlanId = useSetAtom(activePlanIdAtom);
  const sendTrainTogetherModeAction = useSetAtom(trainTogetherModeDispatchAtom);
  const { assignTeamMembers } = useAssignTeamMembers();

  const [showAddTeamMembersMenu, setShowAddTeamMembersMenu] = useState(false);
  const [showAddTeamMembersPopup, setShowAddTeamMembersPopup] = useState(false);
  const [showDueDatePopup, setShowDueDatePopup] = useState(false);
  const [showUnassignTeamMemberPopup, setShowUnassignTeamMemberPopup] =
    useState(false);
  const [activeTeamMember, setActiveTeamMember] = useState({
    id: '',
    name: '',
  });
  const [showPlanInUseModal, setShowPlanInUseModal] = useState(false);
  const [filterAndSortButtonIsOpen, setFilterAndSortButtonIsOpen] =
    useState(false);

  const setDataSource = useSetAtom(dataSource);

  const isPlanQueryEnabled = !!planId;
  const isReportPlanQueryEnabled = !!planId && reportsRoute;
  // @TODO: Migrate off browser-router for route-level validation

  const isValidPlanId = checkIsValidPlanId(planId);

  const [
    {
      isFetching: isFetchingPlanAndAssignments,
      isPending: isPendingPlanAndAssignments,
      error: errorGettingPlan,
      refetch: _refetchPlanAndAssignments,
      data: planAndAssignments,
    },
    {
      error: errorGettingUsers,
      isFetching: isFetchingUsers,
      isPending: isPendingUsers,
      data: users,
    },
    {
      data: planReport,
      isFetching: isFetchingPlanReport,
      isPending: isPendingPlanReport,
    },
  ] = useQueries({
    queries: [
      {
        ...getPlanAndAssignmentsOptions({ path: { planId: safePlanId } }),
        enabled: isPlanQueryEnabled && isValidPlanId,
      },
      {
        ...getUsersForLocationsOptions({
          query: { locationNumbers: locationsWithAtLeastTrainer },
        }),
      },
      {
        ...getPlanReportOptions({
          path: { planId: safePlanId },
          query: {
            locationNumbers: activeLicenseeLocation
              ? [activeLicenseeLocation]
              : undefined,
          },
        }),
        enabled: isReportPlanQueryEnabled && isValidPlanId,
      },
    ],
  });

  const isCompliance =
    planAndAssignments?.plan?.type === 'COMPLIANCE' ||
    planAndAssignments?.plan?.type === 'ANCHOR';

  // Guard with isPlanQueryEnabled: usePlansCertificatesData also calls
  // getPlanAndAssignmentsOptions, populating the cache even when this query is
  // disabled. TanStack Query serves cached
  // data to disabled queries, so without this guard planAndAssignments would be
  // non-null and leak XP plan data into the Add Team Members popup, causing it
  // to treat all members as already assigned.
  const activePlan = isPlanQueryEnabled ? planAndAssignments : undefined;

  const planName = activePlan?.plan?.name ?? planReport?.name;
  const isTrainTogetherEnabled =
    hasTeamMembersAssignedToPlan &&
    activePlan?.plan?.permissions?.trainTogether;

  const refetchPlanAndAssignments = useEffectEvent(() => {
    if (!isValidPlanId) {
      throw new Error('Invalid planId');
    }
    return _refetchPlanAndAssignments();
  });

  const isFetching =
    (isPlanQueryEnabled &&
      (isFetchingPlanAndAssignments || isPendingPlanAndAssignments)) ||
    (isReportPlanQueryEnabled &&
      (isFetchingPlanReport || isPendingPlanReport)) ||
    isFetchingUsers ||
    isPendingUsers;

  useEffect(() => {
    if (planReport) {
      const usersById = new Map((users ?? []).map(u => [u.adId ?? '', u]));
      setDataSource(planUserReportsToTeamMemberRows(planReport, usersById));
      return;
    }

    if (!reportsRoute && users && activePlan) {
      const usersById = new Map(users.map(u => [u.adId ?? '', u]));
      const totalSteps = activePlan.plan?.totalSteps ?? 0;
      setDataSource(
        planAssignmentsToTeamMemberRows(
          activePlan.assignments ?? [],
          usersById,
          totalSteps,
        ),
      );
      return;
    }
  }, [activePlan, planReport, reportsRoute, setDataSource, users]);

  const { getLocationsByPlan, isReady: isLocationsReady } = usePlanLocations();

  const filterLocations = activePlan?.plan
    ? getLocationsByPlan(activePlan.plan)
    : isCompliance
      ? (auth?.assignableTrainerLocations ?? [])
      : [];
  const canShowLocations = isLocationsReady && filterLocations.length > 1;

  // Trigger print report modal with plan details
  const handlePrintReport = () => {
    if (activePlan?.plan) {
      printPlan(activePlan.plan);
      return;
    }
    // Prefer activePlan when present; fall back to plan report payload for print.
    if (planReport) {
      printPlan({
        id: planReport.id,
        name: planReport.name,
        type: planReport.type,
        category: planReport.category,
        ownerId: planReport.ownerId,
        ownerType: planReport.ownerType,
      });
      return;
    }
  };

  // Clear Team Members Filters
  useEffect(() => {
    resetFilters();
    return () => {
      patchTrainTogetherMode({ hasTeamMembersAssignedToPlan: false });
    };
  }, [patchTrainTogetherMode, resetFilters]);

  useEffect(() => {
    if (planId && isValidPlanId) {
      setActivePlanId(planId);
    }
  }, [planId, setActivePlanId, isValidPlanId]);

  const planAssignments =
    activePlan?.assignments ?? planReport?.planUserReports;
  useEffect(() => {
    if (planAssignments) {
      const allAssigneesCompleted = planAssignments
        .filter(it => it.status !== Constants.TRAINING_PLANS.UNASSIGNED)
        .every(user => user.status === Constants.TRAINING_PLANS.COMPLETED);
      patchTrainTogetherMode({
        allAssigneesCompleted,
        hasTeamMembersAssignedToPlan: planAssignments.length > 0,
      });
    }
  }, [patchTrainTogetherMode, planAssignments]);

  useEffect(() => {
    // We can't call `refetchAssignedTeamMembers` in compliance plans because we don't call `useGetAssignedStatusQuery` on component mount
    // Also guard against invalid planId since the query would be skipped
    if (isCompliance || !isValidPlanId || !state?.refetch) {
      return;
    }
    refetchPlanAndAssignments();
    // After we refetch the team members, we set the state back to false
    if (search) {
      navigate(
        { search },
        {
          replace: true,
          state: { refetch: false },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.refetch, isCompliance, isValidPlanId]);

  // GET Team Members By Location Fetch error
  if (isApiError(errorGettingUsers)) {
    datadogRum.addError(new Error(errorGettingUsers.message), {
      context: JSON.stringify(errorGettingUsers),
    });
    return <GenericError />;
  }

  // GET Assigned Team Members Fetch error
  if (isApiError(errorGettingPlan)) {
    datadogRum.addError(new Error(errorGettingPlan.message), {
      context: JSON.stringify(errorGettingPlan),
    });
    return <GenericError />;
  }

  const onAssignTeamMembers = (members: string[], dueDate: string | null) => {
    const dueDateToSend = dueDate as string | undefined;
    const userIds = [...new Set(members)];
    const isFirstAssignment = !hasActiveAssignees(planAssignments);
    assignTeamMembers({
      planId: safePlanId,
      userIds,
      dueDate: dueDateToSend,
      isFirstAssignment,
    })
      .then(async () => {
        const count = userIds.length;
        await invalidateAllPlanQueries(safePlanId);
        invalidateCertificateQueries(safePlanId, userIds);
        showAssignmentSuccessToast(count, t);
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  const onUpdateTeamMemberDueDate = (
    members: string | string[],
    dueDate: string | null,
  ) => {
    // null has to be passed here to clear the due date
    const dueDateToSend = dueDate as string | undefined;
    const payload = Array.isArray(members)
      ? { userIds: [...new Set(members)], dueDate: dueDateToSend }
      : { userIds: [members], dueDate: dueDateToSend };
    updateDueDate({ path: { planId: safePlanId }, body: payload })
      .then(async () => {
        await invalidateAllPlanQueries(safePlanId);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {t('TrainingPlans.toastMessage.updateDueDate', {
              teamMember: activeTeamMember.name,
            })}
          </ToastMessageBlock>
        ));
        onClose();
      })
      .catch((err: unknown) => {
        datadogRum.addError(
          err instanceof Error ? err : new Error(String(err)),
        );
      });
  };

  const onSaveDueDate = (dueDate: string | null) => {
    setShowDueDatePopup(false);
    onUpdateTeamMemberDueDate(activeTeamMember.id, dueDate);
  };

  const onAddTeamMember = () => {
    setShowAddTeamMembersMenu(false);
    setShowAddTeamMembersPopup(true);
  };

  const onUnassign = (id: string, name: string) => {
    setActiveTeamMember({ id, name });
    setShowUnassignTeamMemberPopup(true);
  };

  const onClose = () => {
    setShowAddTeamMembersMenu(false);
    setShowAddTeamMembersPopup(false);
    setShowDueDatePopup(false);
  };

  const onUpdateDueDate = (id: string, name: string) => {
    setActiveTeamMember({ id, name });
    setShowDueDatePopup(true);
  };

  const filterAndSortButtonOptions: FilterAndSortButtonProps = {
    onSortChange: setSort,
    onOpenChange: open => {
      setFilterAndSortButtonIsOpen(open);
    },
    isOpen: filterAndSortButtonIsOpen,
    sortOptions,
    sortValue: sortOptions.find(
      option =>
        option.key === sort?.key && option.direction === sort?.direction,
    ),
    text: `${t('TrainingPlans.filtering.show')} ${
      items.length
    } ${t('TrainingPlans.filtering.results')}`,
  };

  const locationOptions: FilterCheckboxSectionProps = {
    title: t('Generic.locations'),
    labels: filterLocations.reduce(
      (acc, loc) => ({
        ...acc,
        [loc]: {
          translationString: loc,
          value: currentFilters.includes(loc),
        },
      }),
      {},
    ),
    onToggle: v => toggleCheckboxFilter('locations', v),
  };

  const statusOptions: FilterCheckboxSectionProps = {
    title: t('Generic.status'),
    labels: statusLabels,
    onToggle: v => {
      toggleCheckboxFilter('filterStatus', v);
    },
  };

  const searchOptions: FilterSearchBarProps = {
    onChange: (e: React.ChangeEvent<TextFieldType>) =>
      setSearch(e.target.value, 'name'),
    onClear: () => resetSearch(),
    placeholder: t('TrainingPlans.teamMembers.searchbarPlaceholder'),
    value: filters.search,
  };

  const filterOptions = {
    filterAndSortButtonOptions,
    before:
      auth?.isLicensee === false && planAndAssignments?.plan?.autoAssign ? (
        <MessageBlock.Root>
          {t('Generic.planIsAutoAssignedToAllNewHires')}
        </MessageBlock.Root>
      ) : undefined,
    search: <Filter.SearchBar {...searchOptions} />,
    children: (
      <>
        {canShowLocations && (
          <>
            <Filter.CheckboxSection {...locationOptions} />
            <Filter.Divider />
          </>
        )}
        <Filter.CheckboxSection {...statusOptions} />
      </>
    ),
  };

  if (!isValidPlanId && planId) {
    return <Navigate to={routes.plans.plans} replace />;
  }

  return (
    <>
      <Header.Root>
        <Header.Title>{itemByLanguage(planName)}</Header.Title>
        <Header.Back
          to={reportsRoute ? routes.reports.plans : routes.plans.managedPlans}
          tx={
            reportsRoute
              ? 'Reports.tabPlans'
              : 'TrainingPlans.filtering.managePlans'
          }
        />
        {!reportsRoute && (
          <Header.ActionIconButton
            icon={IconUsersGroup}
            tooltip={
              !activePlan?.plan?.permissions?.trainTogether
                ? 'TrainingPlans.trainTogether.notAvailable'
                : isTrainTogetherEnabled
                  ? 'TrainingPlans.trainTogether.toolTip'
                  : 'TrainingPlans.trainTogether.toolTipDisabled'
            }
            text="Button.trainTogether"
            disabled={!isTrainTogetherEnabled}
            onClick={() => {
              track(AmplitudeProductEvent.SHOW_TRAIN_TOGETHER_MODE, {
                callSite: 'EditPlan',
                planId: safePlanId,
                isComplianceOrAnchor: isCompliance,
                locations: filterLocations,
              });
              sendTrainTogetherModeAction('showTrainTogetherMode');
            }}
          />
        )}
        {!reportsRoute && (
          <Header.ActionIconButton
            as="link"
            className={cn(
              'transition-opacity!',
              activePlan?.plan ? 'opacity-100!' : 'opacity-0!',
            )}
            to={routes.plans.managePlansEdit({ planId: safePlanId })}
            icon={activePlan?.plan?.readonly ? Show : IconEdit}
            tooltip={
              activePlan?.plan?.readonly
                ? 'Generic.viewPlanContent'
                : activePlan?.plan
                  ? 'Generic.editPlanContent'
                  : undefined
            }
            disabled={
              !safePlanId ||
              (activePlan?.plan?.permissions?.edit === false &&
                !activePlan.plan.readonly) ||
              !activePlan?.plan
            }
            onClick={e => {
              if (!activePlan?.plan?.readonly && hasTeamMembersAssignedToPlan) {
                e.preventDefault();
                setShowPlanInUseModal(true);
              }
            }}
          />
        )}
        {certificateHook.shouldShow && (
          <Header.ActionIconButton
            data-testid="certificate-icon-button"
            icon={IconCertificate}
            onClick={certificateHook.handleClick}
            tooltip="Button.getCertificates"
            disabled={certificateHook.isLoading}
          />
        )}
        <Header.ActionIconButton
          icon={IconPrinter}
          tooltip="Button.printReport"
          onClick={handlePrintReport}
        />
      </Header.Root>
      <LoadingOverlay isOpen={isFetching} fixed />
      <div className="md:mt-header-height-with-content-spacing-lg centered-content px-4 max-md:mt-6">
        <div className="flex max-w-full flex-col">
          {!isFetching && <FilterMobile {...filterOptions} />}
          {/* Top Row */}
          <div className="flex-end flex md:pl-[304px]">
            <SortFilterHeader
              label={t('TrainingPlans.filtering.sortBy')}
              onChange={setSort}
              onClear={() => {
                resetAllCheckboxFilters();
              }}
              options={sortOptions}
              showClear={false}
              showCompletedPlansOption={false}
              showMyCompletedPlansOnlySwitchValue={false}
              text={`${total} ${t('Generic.teamMembers')}`}
              title={
                reportsRoute
                  ? t('Generic.teamMembers')
                  : itemByLanguage(planName)
              }
              total={total}
              value={sortOptions.find(
                option =>
                  option.key === sort?.key &&
                  option.direction === sort?.direction,
              )}
              mobileRightSideAction={
                <PopoverMenu.Root
                  triggerButtonClassName="mr-2!"
                  panelProps={{ containerPadding: 25 }}
                >
                  {!reportsRoute && (
                    <>
                      <PopoverMenu.Action
                        icon={IconUserPlus}
                        tx="Generic.assign"
                        onClick={() => onAddTeamMember()}
                      />
                      <PopoverMenu.Action
                        as="link"
                        to={routes.plans.managePlansEdit({
                          planId: safePlanId,
                        })}
                        icon={activePlan?.plan?.readonly ? Show : IconEdit}
                        tx={
                          activePlan?.plan?.readonly
                            ? 'Generic.viewPlanContent'
                            : 'Generic.editPlanContent'
                        }
                        disabled={
                          !safePlanId ||
                          (activePlan?.plan?.permissions?.edit === false &&
                            !activePlan.plan.readonly) ||
                          !activePlan?.plan
                        }
                      />
                      <PopoverMenu.Action
                        icon={IconUsersGroup}
                        tx="Button.trainTogether"
                        onClick={() => {
                          track(
                            AmplitudeProductEvent.SHOW_TRAIN_TOGETHER_MODE,
                            {
                              callSite: 'EditPlan',
                              planId: safePlanId,
                              isComplianceOrAnchor: isCompliance,
                              locations: filterLocations,
                            },
                          );
                          sendTrainTogetherModeAction('showTrainTogetherMode');
                        }}
                        disabled={!isTrainTogetherEnabled}
                      />
                      <PopoverMenu.Divider />
                    </>
                  )}
                  {certificateHook.shouldShow && (
                    <PopoverMenu.Action
                      icon={IconCertificate}
                      tx="Button.getCertificates"
                      onClick={certificateHook.handleClick}
                      disabled={certificateHook.isLoading}
                      data-testid="certificate-button-mobile"
                    />
                  )}
                  <PopoverMenu.Action
                    icon={IconPrinter}
                    tx="Button.printReport"
                    onClick={handlePrintReport}
                  />
                  <PopoverMenu.Divider />
                  <PopoverMenu.Action
                    icon={IconFilter}
                    tx="TrainingPlans.filtering.filterAndSort"
                    onClick={() => setFilterAndSortButtonIsOpen(true)}
                  />
                </PopoverMenu.Root>
              }
            />
          </div>
          {/* Bottom Row */}
          <div className="mt-2.5 flex justify-between">
            {/* Left Side */}
            {!isFetching && <FilterDesktop {...filterOptions} />}
            {/* Right Side */}
            <div className="w-full">
              <div className="relative flex max-w-full gap-6 pt-1 pl-1">
                <div className="flex grow flex-col">
                  <div className="relative flex max-w-full grow flex-col gap-4">
                    {!!items.length &&
                      items.map(member => (
                        <TeamMemberCard
                          key={member.id}
                          member={{
                            ...member,
                            name: member.displayName || member.name || '',
                          }}
                          plan={activePlan}
                          planReport={planReport}
                          reportsRoute={reportsRoute}
                          onUnassign={onUnassign}
                          onUpdateDueDate={onUpdateDueDate}
                        />
                      ))}
                    {!items.length &&
                      !currentFilters.length &&
                      !reportsRoute && (
                        <p
                          className="leading-snug"
                          dangerouslySetInnerHTML={{
                            __html: t(
                              'TrainingPlans.noTeamMembersAssignedManagePlans',
                            ),
                          }}
                        />
                      )}
                    {!items.length &&
                      !currentFilters.length &&
                      reportsRoute && (
                        <p
                          className="leading-snug"
                          dangerouslySetInnerHTML={{
                            __html: t('TrainingPlans.noTeamMembersAssigned'),
                          }}
                        />
                      )}
                    {!!currentFilters.length && !items.length && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: t('TrainingPlans.noTeamMembersResults'),
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!reportsRoute && (
        <>
          <AddTeamMemberFab
            onAddTeamMember={onAddTeamMember}
            setShowAddTeamMembersMenu={setShowAddTeamMembersMenu}
            showAddTeamMembersMenu={showAddTeamMembersMenu}
          />
          <UnassignTeamMember
            activeTeamMember={activeTeamMember}
            isOpen={showUnassignTeamMemberPopup}
            onClose={() => {
              setShowUnassignTeamMemberPopup(false);
              setActiveTeamMember({ id: '', name: '' });
            }}
            planId={safePlanId}
          />
          <DueDatePopUp
            isOpen={showDueDatePopup}
            onClose={onClose}
            onSave={date => onSaveDueDate(date)}
          />
          <AssignTeamMembersModal
            isOpen={showAddTeamMembersPopup}
            onClose={onClose}
            plan={planAndAssignments?.plan}
            isCompliance={isCompliance}
            reassignEligible
            onAssign={onAssignTeamMembers}
          />
          {planAndAssignments?.plan && (
            <TrainTogetherModal plan={planAndAssignments.plan} />
          )}
        </>
      )}

      <PrintCertificateModals {...certificateHook} />

      <PlanInUseModal
        isOpen={showPlanInUseModal}
        onClose={() => setShowPlanInUseModal(false)}
        onContinue={() => {
          setShowPlanInUseModal(false);
          navigate(routes.plans.managePlansEdit({ planId: safePlanId }));
        }}
      />
    </>
  );
};

export default withRoles(PlanAssignees, 'plans.manage');
